/* eslint-disable no-nested-ternary */
import { useContext, useMemo } from "react";
import { useTable, useSortBy } from "react-table";
import { IStrategyAccountDetail } from "../../hooks/api/admin/useStrategies";
import { DataContext } from "../../context/DataContext";
import { DefaultCellForColumn } from "../Table/DefaultCellForColumn";
import { DefaultHeaderForColumn } from "../Table/DefaultHeaderForColumn";
import {
  Align,
  MinifiedTableWrapper,
  TableLoader,
  TableHeaderCell,
} from "../Table/style";
import { ITableColumn } from "../Table/TableColumn";
import { COLORS, TEXT_COLORS } from "../../constants/design/colors";
import { shortenString } from "../../utils/strings";

interface IStrategyAccountsTableProps {
  accounts?: IStrategyAccountDetail[];
  isLoading?: boolean;
}

// Renders "0" and empty amounts dimmed so the eye lands on the accounts
// that actually have something pending/committed/in-flight.
const isZero = (value?: string) => !value || Number(value) === 0;

export function StrategyAccountsTable({
  accounts,
  isLoading,
}: IStrategyAccountsTableProps) {
  const { records } = useContext(DataContext);

  const data: IStrategyAccountDetail[] = useMemo(
    () => accounts || [],
    [accounts]
  );

  // Amount cell that dims zeroes and (optionally) tints non-zero values so
  // pending deposits/withdrawals pop out of the row.
  const amountCell = (color?: string) =>
    // eslint-disable-next-line react/no-unstable-nested-components
    function AmountCell({ value }: { value: string }) {
      const zero = isZero(value);
      return (
        <Align
          align="right"
          style={{ color: zero ? TEXT_COLORS.three : color || TEXT_COLORS.one }}
        >
          {value ?? "0"}
        </Align>
      );
    };

  // Count cell that dims zeroes so a non-zero in-process count stands out.
  const countCell =
    // eslint-disable-next-line react/no-unstable-nested-components
    function CountCell({ value }: { value: number }) {
      return (
        <Align
          align="right"
          style={{ color: value > 0 ? COLORS.orange.one : TEXT_COLORS.three }}
        >
          {value}
        </Align>
      );
    };

  const columns: ITableColumn<IStrategyAccountDetail>[] = useMemo(
    () => [
      {
        title: "Account",
        align: "left",
        accessor: "account",
        // eslint-disable-next-line react/no-unstable-nested-components
        Cell: ({ value }: { value: string }) => {
          const name = records[value];
          return (
            <Align align="left">
              <a
                href={`/account/${value}`}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  color: name ? COLORS.blue.one : TEXT_COLORS.one,
                  textDecoration: "none",
                }}
              >
                {name || shortenString(value, 6, 4)}
              </a>
            </Align>
          );
        },
      },
      { title: "Shares", align: "right", accessor: "shares" },
      { title: "Avg PPS", align: "right", accessor: "average_pps" },
      {
        title: "Pending Deposit",
        align: "right",
        accessor: "pending_deposit",
        Cell: amountCell(COLORS.green.one),
      },
      {
        title: "Pending Withdrawal",
        align: "right",
        accessor: "pending_withdrawal",
        Cell: amountCell(COLORS.red.one),
      },
      {
        title: "Committed Deposit",
        align: "right",
        accessor: "commited_deposit",
        Cell: amountCell(),
      },
      {
        title: "Committed Withdrawal",
        align: "right",
        accessor: "commited_withdrawal",
        Cell: amountCell(),
      },
      {
        title: "In-Process Deposits",
        align: "right",
        accessor: "in_process_deposit_count",
        Cell: countCell,
      },
      {
        title: "In-Process Withdrawals",
        align: "right",
        accessor: "in_process_withdrawal_count",
        Cell: countCell,
      },
    ],
    [records]
  );

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } =
    useTable(
      {
        columns,
        data,
        defaultColumn: {
          Header: DefaultHeaderForColumn,
          Cell: DefaultCellForColumn,
        } as any,
        autoResetSortBy: false,
      },
      useSortBy
    );

  return (
    <MinifiedTableWrapper>
      <TableLoader isLoading={isLoading} />
      <table {...getTableProps()}>
        <thead>
          {headerGroups.map((headerGroup) => (
            // eslint-disable-next-line react/jsx-key
            <tr {...headerGroup.getHeaderGroupProps()}>
              {headerGroup.headers.map((column: any) => (
                // eslint-disable-next-line react/jsx-key
                <TableHeaderCell
                  {...column.getHeaderProps(column.getSortByToggleProps())}
                  style={{ width: column.width }}
                >
                  {column.render("Header")}
                </TableHeaderCell>
              ))}
            </tr>
          ))}
        </thead>
        <tbody {...getTableBodyProps()}>
          {rows.map((row) => {
            prepareRow(row);
            const { ...rowProps } = row.getRowProps();
            return (
              // eslint-disable-next-line react/jsx-key
              <tr {...rowProps}>
                {row.cells.map((cell) => (
                  // eslint-disable-next-line react/jsx-key
                  <td {...cell.getCellProps()}>{cell.render("Cell")}</td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    </MinifiedTableWrapper>
  );
}
